/**
 * Step 2.5: Semantic Match
 * Aligns VI source segments with EN benchmark segments before review
 * Layout similar to SemanticMatchLab (2-column scrollable panels)
 */

import React, { useRef } from 'react';
import { Link2, AlertTriangle, CheckCircle, RefreshCw, ArrowRight, ChevronLeft, FolderOpen } from 'lucide-react';
import type { SemanticMatchResult } from '../../../services';

interface Step25SemanticMatchProps {
    sourceSegments: string[];
    humanSegments: string[];
    semanticMatchResult: SemanticMatchResult | null;
    isMatching: boolean;
    onRunMatch: () => void;
    onContinue: () => void;
    onBack: () => void;
    onReloadFiles?: (viText: string, enText: string) => void;
    onPreviewPrompt?: () => void;
}


const Step25SemanticMatch: React.FC<Step25SemanticMatchProps> = ({
    sourceSegments,
    humanSegments,
    semanticMatchResult,
    isMatching,
    onRunMatch,
    onContinue,
    onBack,
    onPreviewPrompt
}) => {
    const hasUnmatched = semanticMatchResult && semanticMatchResult.unmatchedCount > 0;

    // Build a map of grouped VI segments (shared EN targets)
    // Returns: Map<viIndex, { groupId: string, isFirst: boolean, groupMembers: number[] }>
    const buildGroupedSegments = () => {
        if (!semanticMatchResult) return new Map<number, { groupId: string; isFirst: boolean; groupMembers: number[] }>();

        // Group mappings by their EN target signature (sorted allHumanIndices as key)
        const enTargetToVIs: Map<string, number[]> = new Map();

        for (const mapping of semanticMatchResult.mappings) {
            if (mapping.isUnmatched || mapping.allHumanIndices.length === 0) continue;

            const key = [...mapping.allHumanIndices].sort((a, b) => a - b).join(',');
            const existing = enTargetToVIs.get(key) || [];
            existing.push(mapping.sourceIndex);
            enTargetToVIs.set(key, existing);
        }

        // Build result map
        const result = new Map<number, { groupId: string; isFirst: boolean; groupMembers: number[] }>();

        for (const [enKey, viIndices] of enTargetToVIs.entries()) {
            // Only consider groups with 2+ VI segments
            if (viIndices.length > 1) {
                const sortedVIs = viIndices.sort((a, b) => a - b);
                for (let i = 0; i < sortedVIs.length; i++) {
                    result.set(sortedVIs[i], {
                        groupId: enKey,
                        isFirst: i === 0,
                        groupMembers: sortedVIs
                    });
                }
            }
        }

        return result;
    };

    const groupedSegments = buildGroupedSegments();

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4 shrink-0 bg-white">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="text-slate-400 hover:text-slate-600 flex items-center gap-1 text-xs font-semibold"
                    >
                        <ChevronLeft className="w-4 h-4" /> Quay lại
                    </button>
                    <div>
                        <h2 className="text-lg font-black text-slate-800 flex items-center gap-2">
                            <Link2 className="text-violet-500 w-5 h-5" /> Đối chiếu ngữ nghĩa
                        </h2>
                        <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">
                            {sourceSegments.length} đoạn VI · {humanSegments.length} đoạn EN
                        </p>
                    </div>
                </div>
                <div className="flex gap-3 items-center">
                    {semanticMatchResult && (
                        <div className={`text-xs font-bold px-3 py-1.5 rounded-lg ${hasUnmatched ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {hasUnmatched ? (
                                <span className="flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {semanticMatchResult.unmatchedCount} chưa khớp
                                </span>
                            ) : (
                                <span className="flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Đã khớp hết
                                </span>
                            )}
                        </div>
                    )}
                    {onPreviewPrompt && (
                        <button
                            onClick={onPreviewPrompt}
                            className="bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 px-4 py-2 rounded-lg font-bold flex items-center gap-2 text-xs"
                            title="Xem trước prompt AI"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Xem trước
                        </button>
                    )}
                    <button
                        onClick={onRunMatch}
                        disabled={isMatching}
                        className="bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 text-xs whitespace-nowrap"
                    >
                        {isMatching ? <RefreshCw className="animate-spin w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                        {isMatching ? 'Đang đối chiếu...' : semanticMatchResult ? 'Đối chiếu lại' : 'Bắt đầu đối chiếu'}
                    </button>
                    <button
                        onClick={onContinue}
                        disabled={!semanticMatchResult || isMatching}
                        className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 text-xs whitespace-nowrap"
                    >
                        Tiếp tục <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* 3-Column Layout like SemanticMatchLab */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left: Vietnamese (Source) */}
                <div className="w-1/3 border-r border-slate-200 flex flex-col">
                    <div className="h-10 bg-slate-100 border-b border-slate-200 flex items-center px-4">
                        <span className="text-sm font-bold text-slate-600">Tiếng Việt (Bản gốc)</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        {sourceSegments.map((seg, idx) => {
                            const mapping = semanticMatchResult?.mappings.find(m => m.sourceIndex === idx);
                            const isUnmatched = semanticMatchResult ? (mapping?.isUnmatched ?? true) : false;
                            const groupInfo = groupedSegments.get(idx);

                            // Build index display and tooltip
                            let indexDisplay = '';
                            let indexTooltip = '';
                            if (mapping && !mapping.isUnmatched) {
                                const indices = mapping.allHumanIndices.map(i => i + 1);
                                if (indices.length === 1) {
                                    indexDisplay = `EN-${indices[0]}`;
                                } else {
                                    indexDisplay = `EN-${indices[0]} +${indices.length - 1}`;
                                }
                                indexTooltip = `Matched: EN-${indices.join(', EN-')}`;
                            }

                            return (
                                <div
                                    key={idx}
                                    className={`p-2 mb-2 rounded-lg border text-xs relative ${isUnmatched && semanticMatchResult
                                        ? 'bg-red-50 border-red-200'
                                        : mapping && !mapping.isUnmatched
                                            ? mapping.confidence >= 80 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
                                            : 'bg-white border-slate-200'
                                        } ${groupInfo ? 'border-l-4 border-l-violet-400' : ''}`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-slate-600">[VI-{idx + 1}]</span>
                                            {groupInfo && (
                                                <span
                                                    className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-600 cursor-help"
                                                    title={`Nhóm chung: VI-${groupInfo.groupMembers.map(i => i + 1).join(', VI-')} → EN-${groupInfo.groupId.split(',').map(i => parseInt(i) + 1).join(', EN-')}`}
                                                >
                                                    {groupInfo.isFirst ? 'NHÓM' : `cùng VI-${groupInfo.groupMembers[0] + 1}`}
                                                </span>
                                            )}
                                        </div>
                                        {mapping && !mapping.isUnmatched && (
                                            <span
                                                className={`cursor-help text-[10px] px-1.5 py-0.5 rounded ${mapping.confidence >= 80 ? 'bg-emerald-200 text-emerald-700' : 'bg-amber-200 text-amber-700'}`}
                                                title={indexTooltip}
                                            >
                                                {indexDisplay} ({mapping.confidence}%)
                                            </span>
                                        )}
                                        {isUnmatched && semanticMatchResult && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-200 text-red-700 flex items-center gap-1">
                                                <AlertTriangle className="w-3 h-3" /> CHƯA KHỚP
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-slate-700 whitespace-pre-wrap">{seg}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Middle: ALL EN Segments (Reference Pool) */}
                <div className="w-1/3 border-r border-slate-200 flex flex-col">
                    <div className="h-10 bg-slate-100 border-b border-slate-200 flex items-center px-4">
                        <span className="text-sm font-bold text-slate-600">Kho tiếng Anh ({humanSegments.length} đoạn)</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        {humanSegments.map((seg, idx) => (
                            <div key={idx} className="p-2 mb-2 rounded-lg border bg-white border-slate-200 text-xs hover:bg-slate-50">
                                <span className="font-bold text-slate-400 mr-2">[EN-{idx + 1}]</span>
                                <span className="text-slate-700">{seg}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: Matched & Reordered Results */}
                <div className="w-1/3 flex flex-col">
                    <div className="h-10 bg-emerald-50 border-b border-emerald-100 flex items-center px-4">
                        <span className="text-sm font-bold text-emerald-700">Đã khớp & sắp xếp</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        {semanticMatchResult ? (
                            semanticMatchResult.alignedHumanSegments.map((seg, idx) => {
                                const mapping = semanticMatchResult.mappings.find(m => m.sourceIndex === idx);
                                const isUnmatched = mapping?.isUnmatched ?? true;

                                return (
                                    <div
                                        key={idx}
                                        className={`p-2 mb-2 rounded-lg border text-xs ${isUnmatched
                                            ? 'bg-red-50 border-red-200'
                                            : 'bg-emerald-50 border-emerald-200'
                                            }`}
                                    >
                                        <span className={`font-bold mb-1 block ${isUnmatched ? 'text-red-600' : 'text-emerald-600'}`}>
                                            Khớp với [VI-{idx + 1}]
                                        </span>
                                        <p className={`whitespace-pre-wrap ${isUnmatched ? 'text-red-400 italic' : 'text-slate-700'}`}>
                                            {seg}
                                        </p>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center text-slate-400 py-10 italic text-sm">
                                Bấm "Bắt đầu đối chiếu" để xem kết quả
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step25SemanticMatch;
